import { GeneralLoanSettingService } from '@/modules/global-settings/loan-origination/services/loan-policies/general-loan-settings';
import Vue from 'vue';
import { make } from 'vuex-pathify';
// Create a new service instance for the General Loan Settings
const generalSettingService = new GeneralLoanSettingService();
// Declare the state
export const state = {
  settingId: null,
  deviations: [],
  frequencies: {},
  general: {},
  insurance: {},
  limits: {},
  loanStoppers: {},
  yapu: {}
};
// Declare the mutations
export const mutations = {
  ...make.mutations(state),

  ADD_FREQUENCY(state) {
    const existingNames = state.frequencies.map((frequency) =>
      frequency.name.toLowerCase().trim()
    );
    let count = state.frequencies.length + 1;
    let name = `Frequency ${count}`;

    while (existingNames.includes(name.toLowerCase())) {
      count++;
      name = `Frequency ${count}`;
    }

    state.frequencies.push({
      default: false,
      frequencyDisplayInterest: 'Annual',
      frequencyFirstPaymentDays: 1,
      name,
      noun: 'N/A',
      period: 1
    });
  },

  REMOVE_FREQUENCY(state, index) {
    state.frequencies.splice(index, 1);
  },

  UPDATE_LIMITS(state, { limitName, itemIndex, value, headerValue }) {
    Vue.set(state.limits[limitName][itemIndex], headerValue, value);
  }
};
// Declare the actions
export const actions = {
  /**
   * Fetch general loan settings
   * @async
   * @param {Object} context - Vuex context
   * @returns {number | undefined} - HTTP status code
   * @private This will set the general loan settings state
   */
  async getGeneralLoanSettings({ commit }) {
    const result = await generalSettingService.fetchGeneralLoanSettings();
    if (result.status == 200) {
      commit('SET_SETTING_ID', result.data.id);
      commit('SET_DEVIATIONS', result.data.deviations);
      commit('SET_FREQUENCIES', result.data.frequencies);
      commit('SET_GENERAL', result.data.general);
      commit('SET_INSURANCE', result.data.insurance);
      commit('SET_LIMITS', result.data.limits);
      commit('SET_LOAN_STOPPERS', result.data.loanStoppers);
      commit('SET_YAPU', result.data?.yapu || {});
    }
    return result.status;
  },

  /**
   * Update general loan settings
   * @async
   * @param {Object} context - Vuex context
   * @param {Object} payload - General loan settings ID and updates
   * @param {string} payload.id - General loan settings ID
   * @param {Object} payload.updates - General loan settings updates
   * @returns {number | undefined} - HTTP status code
   * @private This will refetch the general loan settings state
   */
  async updateGeneralLoanSettings({ dispatch, state }, { id, updates } = {}) {
    const updateId = id || state.settingId;
    const changes = updates || state;
    const result = await generalSettingService.updateGeneralLoanSettings(
      updateId,
      changes
    );
    if (result.status == 200) {
      dispatch('getGeneralLoanSettings');
    }
    return result.status;
  },

  /**
   * Create general loan payment frequency
   * @async
   * @param {Object} context - Vuex context
   * @returns {number | undefined} - HTTP status code
   * @private This will refetch the general loan settings state
   */
  async createPaymentFrequency({ commit, dispatch, state }) {
    const previousFrequencies = [...state.frequencies];
    commit('ADD_FREQUENCY');
    try {
      const status = await dispatch('updateGeneralLoanSettings');
      if (status == 200) {
        return 201;
      }
      commit('SET_FREQUENCIES', previousFrequencies);
      return status;
    } catch (error) {
      commit('SET_FREQUENCIES', previousFrequencies);
      throw error;
    }
  },

  /**
   * Delete general loan payment frequency
   * @async
   * @param {Object} context - Vuex context
   * @param {number} index - Index of the frequency to delete
   * @returns {number | undefined} - HTTP status code
   * @private This will refetch the general loan settings state
   */
  async deletePaymentFrequency({ commit, dispatch, state }, index) {
    const previousFrequencies = [...state.frequencies];
    commit('REMOVE_FREQUENCY', index);
    try {
      const status = await dispatch('updateGeneralLoanSettings');
      if (status == 200) {
        return 201;
      }
      commit('SET_FREQUENCIES', previousFrequencies);
      return status;
    } catch (error) {
      commit('SET_FREQUENCIES', previousFrequencies);
      throw error;
    }
  }
};
